import React from 'react';
import _ from 'lodash';
import { View, Text, StyleSheet, Image } from '@react-pdf/renderer';

const BORDER_COLOR = '#8A9EAF';
const BORDER_STYLE = 'solid';

const styles = StyleSheet.create({
  torque: {
    fontSize: 9,
    fontFamily: 'Roboto',
    fontWeight: '600',
    lineHeight: '1.5px',
    color: '#6921C4',
  },
  headerContainer: {
    marginBottom: 5,
    flexDirection: 'row',
    alignItems: 'flex-start',
    left: 0,
    right: 0,
    paddingTop: 10,
  },
  flex__start: {
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    flexGrow: 5,
    width: '50%',
  },
  flex__end: {
    width: '50%',
    alignItems: 'flex-end',
  },
  leftColumn: {
    flexDirection: 'column',
    flexGrow: 9,
  },
  tableRow__heading: {
    margin: 'auto',
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: '100%',
  },
  estimated__total__heading: {
    fontSize: 9,
    fontFamily: 'Roboto',
    fontWeight: '600',
    marginTop: 10,
    marginBottom: 5,
  },
  signature__heading: {
    fontSize: 9,
    fontFamily: 'Roboto',
    fontWeight: '600',
    marginTop: 10,
  },
  estimated__total__para: {
    fontSize: 7,
    fontFamily: 'Roboto',
    fontWeight: '400',
    paddingTop: 2,
    lineHeight: '1.6px',
  },
  estimated__total__sig: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 5,
    marginTop: 5,
    border: '1px solid #8A9EAF',
    width: '150px',
    height: '100px',
  },
  imageWrapper: {
    width: '100px',
    height: '100px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  table: {
    display: 'table',
    width: '90%',
    borderStyle: BORDER_STYLE,
    borderColor: BORDER_COLOR,
    borderWidth: 1,
    borderRadius: 0,
    borderBottomWidth: 0,
    overflow: 'hidden',
    marginTop: 10,
  },
  tableCellHeader: {
    margin: 5,
    fontSize: 8,
    fontFamily: 'Roboto',
    fontWeight: '600',
  },
  tableCell: {
    margin: 5,
    fontSize: 7,
    fontFamily: 'Roboto',
    fontWeight: '400',
  },
  tableRow: {
    borderStyle: BORDER_STYLE,
    borderColor: BORDER_COLOR,
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderBottomWidth: 1,
    borderRightWidth: 0,
    borderTopRightRadius: 0,
  },
  table__total: {
    borderStyle: BORDER_STYLE,
    borderColor: BORDER_COLOR,
    borderWidth: 1,
    borderTopWidth: 0,
    borderLeftWidth: 0,
    borderBottomWidth: 1,
    borderRightWidth: 0,
    backgroundColor: 'rgba(231, 236, 244, .8)',
  },
  details__header: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  term__cond__container: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    width: '100%',
    marginRight: 'auto',
  },
});

export default function EstimatedTotal({ data, userId }) {
  const {
    id,
    jobs = [],
    currencySymbol = '$',
    termsAndConditions = '',
    addCommasToNumber,
    estimateTemplate = [],
    appliedTaxes = [],
  } = data || '';

  const sigDirName = `roSignatures/${process.env.REACT_APP_ENV}/${userId}/${id}`;
  const signatureLink = `${process.env.REACT_APP_AWS_S3_URL}/${sigDirName}/roSignature.jpeg`;

  let subTotal = 0;
  let discount = 0;
  let totalTax = 0;
  let totalCost = 0;
  let labourSub = 0;
  let partSupplySub = 0;
  let miscSub = 0;

  labourSub += _.sumBy(jobs, (o) => +o.labourSubTotal || 0);
  labourSub = addCommasToNumber?.(labourSub);
  partSupplySub += _.sumBy(jobs, (o) => +o.partSupplySubTotal || 0);
  partSupplySub = addCommasToNumber?.(partSupplySub);
  miscSub += _.sumBy(jobs, (o) => +o.miscSubTotal || 0);
  miscSub = addCommasToNumber?.(miscSub);
  subTotal += _.sumBy(jobs, (o) => +o.subTotal || 0);
  discount += _.sumBy(jobs, (o) => +o.discount || 0);
  totalTax += _.sumBy(jobs, (o) => +o.totalTax || 0);
  totalCost += _.sumBy(jobs, (o) => +o.totalCost || 0);

  subTotal = addCommasToNumber?.(Math.round((+subTotal || 0) * 100) / 100 || 0);
  discount = addCommasToNumber?.(Math.round((+discount || 0) * 100) / 100 || 0);
  totalTax = addCommasToNumber?.(Math.round((+totalTax || 0) * 100) / 100 || 0);
  totalCost = addCommasToNumber?.(
    Math.round((+totalCost || 0) * 100) / 100 || 0,
  );

  // making estimate according to estimate template
  const grandTotalsInfo =
    estimateTemplate.find(
      (temp) => temp.title?.toLowerCase() === 'grand totals',
    )?.options || '';

  const termsAndCondCheck =
    estimateTemplate.find(
      (temp) => temp.title?.toLowerCase() === 'terms and conditions',
    )?.active || false;
  return (
    <>
      <View style={styles.headerContainer}>
        <View style={styles.flex__start}>
          {termsAndCondCheck && (
            <View style={styles.term__cond__container} wrap>
              <Text style={styles.estimated__total__heading}>
                Terms & Conditions:
              </Text>
              <Text style={styles.estimated__total__para}>
                {termsAndConditions}
              </Text>
            </View>
          )}
          <View style={styles.tableRow__heading} wrap={false}>
            <Text style={styles.signature__heading}>Signature</Text>
            <View style={styles.estimated__total__sig}>
              <View style={styles.imageWrapper}>
                <Image
                  style={{
                    width: '140px',
                    height: '100px',
                    objectFit: 'fill',
                  }}
                  src={signatureLink}
                />
              </View>
            </View>
          </View>
        </View>
        <View style={styles.flex__end} wrap={false}>
          <View style={styles.table}>
            <View style={styles.tableRow}>
              {grandTotalsInfo?.['Total Labor'] && (
                <View style={styles.details__header}>
                  <Text style={styles.tableCell}>Total Labor</Text>
                  <Text style={styles.tableCellHeader}>
                    {currencySymbol}
                    {labourSub}
                  </Text>
                </View>
              )}
              {grandTotalsInfo?.['Total Parts & Supplies'] && (
                <View style={styles.details__header}>
                  <Text style={styles.tableCell}>Total Parts & Supplies</Text>
                  <Text style={styles.tableCellHeader}>
                    {currencySymbol}
                    {partSupplySub}
                  </Text>
                </View>
              )}
              {grandTotalsInfo?.['Total Miscellaneous'] && (
                <View style={styles.details__header}>
                  <Text style={styles.tableCell}>Total Miscellaneous</Text>
                  <Text style={styles.tableCellHeader}>
                    {currencySymbol}
                    {miscSub}
                  </Text>
                </View>
              )}
              {grandTotalsInfo?.['Subtotal'] && (
                <View style={styles.details__header}>
                  <Text style={styles.tableCell}>Subtotal</Text>
                  <Text style={styles.tableCellHeader}>
                    {currencySymbol}
                    {subTotal}
                  </Text>
                </View>
              )}
              {grandTotalsInfo?.['Total Discount'] && (
                <View style={styles.details__header}>
                  <Text style={styles.tableCell}>Total Discount</Text>
                  <Text style={styles.tableCellHeader}>
                    {currencySymbol}
                    {discount}
                  </Text>
                </View>
              )}
              {grandTotalsInfo?.['Total Tax'] && (
                <View style={styles.details__header}>
                  <Text style={styles.tableCell}>Total Tax</Text>
                  <Text style={styles.tableCellHeader}>
                    {currencySymbol}
                    {totalTax}
                  </Text>
                </View>
              )}
              {grandTotalsInfo?.['Total Tax'] &&
                appliedTaxes?.map((tax) => {
                  return (
                    <View style={styles.details__header}>
                      <Text style={[styles.tableCellHeader]}>
                        {tax?.name}: {currencySymbol}
                        {tax?.calculatedTax}
                      </Text>
                    </View>
                  );
                })}
            </View>

            <View style={styles.table__total}>
              {grandTotalsInfo?.['Estimated Total'] && (
                <View style={styles.details__header}>
                  <Text style={styles.tableCellHeader}>Estimated Total</Text>
                  <Text style={styles.tableCellHeader}>
                    {currencySymbol}
                    {totalCost}
                  </Text>
                </View>
              )}
            </View>
          </View>
        </View>
      </View>
    </>
  );
}
